/**
 * The briefing an embedded webapp's author hands to their coding agent.
 *
 * This is deliberately NOT the same artefact as the snippets in the slide's
 * settings drawer. Those are reference: here is the API, look it up. This is an
 * instruction: do these things, in this order, don't make these three mistakes,
 * and here is how you know it worked. Most people embedding a webapp in Kula
 * built it by prompting an agent, so the prompt is the real delivery format and
 * the snippets are the fallback for someone hand-writing.
 *
 * Keep this the single source of the author-facing contract. The failure mode to
 * design against is a prompt that outlives the protocol — someone pastes a copy
 * saved months ago and gets confidently wrong instructions with no error to
 * explain it. Anything added to the embed protocol belongs here the same day.
 */

/** Stand-in used when the slide has no URL yet, so the prompt still reads. */
const URL_PLACEHOLDER = 'https://your-app.vercel.app';

export interface EmbedAuthorPromptOptions {
  /** The URL configured on the slide. Load-bearing: it IS the origin rule. */
  url?: string;
}

import { embedOriginFor } from './embedProtocol';

// Re-exported so existing callers keep one import site; the rule itself lives in
// embedProtocol alongside the checker that reports on it.
export { embedOriginFor };

export function buildEmbedAuthorPrompt({ url }: EmbedAuthorPromptOptions = {}): string {
  const origin = embedOriginFor(url);
  const shown = url?.trim() || URL_PLACEHOLDER;

  const originLine = origin
    ? `This app is embedded from **${shown}**, so the lesson will only accept messages sent from the origin \`${origin}\`. Serve it from there — not a preview deployment, not a redirect to somewhere else, not localhost.`
    : `This app is embedded from **${shown}**. The lesson only accepts messages from that exact origin — not a preview deployment, not a redirect, not localhost. Note the URL must include \`https://\`; without a scheme the lesson cannot work out an origin and silently drops every message.`;

  return `Wire this web app into a Kula lesson.

It runs inside an iframe on a lesson slide. Right now the learner has to find a small "Done" button in the lesson's top bar to move on — after ten minutes working inside your app, nobody looks up there. Your job is to make the app hand the learner back properly.

## The channel

Talk to the lesson with \`window.parent.postMessage\`. Every message has this shape:

\`\`\`js
{ type: 'lms-embed-event', version: '1.0', payload: { event: <name> } }
\`\`\`

Send three events.

**1. \`ready\` — as soon as the app loads.** The lesson replies with a separate \`lms-init\` message carrying the learner's details, so you can greet them by name or save their work against them. Listen for it; do not block on it (see the standalone rule below).

\`\`\`js
window.parent.postMessage({
  type: 'lms-embed-event', version: '1.0', payload: { event: 'ready' }
}, '*');

window.addEventListener('message', (e) => {
  if (e.source !== window.parent) return;   // only the lesson may speak
  if (e.data?.type !== 'lms-init') return;
  const user = e.data.payload?.user;
  if (!user?.id) return;                    // ignore anything malformed
  // user: { id, name, firstName, lastName, email, role }
  const state = e.data.payload?.state;      // see "Saved state" below; may be null
});
\`\`\`

Keep both guards. \`message\` is a global event — any frame that can reach your window can fire one, so an unchecked handler will happily accept a made-up learner identity. Never trust the payload's shape either; treat a message that fails either check as noise and carry on.

**2. \`completed\` — when the learner REACHES your closing screen.** This marks the activity done for progress tracking. Include \`score\` (0–100) if the activity produces one.

\`\`\`js
window.parent.postMessage({
  type: 'lms-embed-event', version: '1.0',
  payload: { event: 'completed', score: 85 }
}, '*');
\`\`\`

**If the activity is marked, send the marks with \`completed\`.** Add a \`result\` with the raw marks (not a percentage), a fresh \`attemptId\` for each run-through, and, if you can, one entry per question. Kula keeps every attempt, so this is how a teacher sees a learner improve over time.

\`\`\`js
window.parent.postMessage({
  type: 'lms-embed-event', version: '1.0',
  payload: {
    event: 'completed',
    score: 70,                      // 0–100, rounded
    result: {
      attemptId: crypto.randomUUID(), // new for every run-through, same if you re-send
      earned: 14,
      possible: 20,
      items: [                       // optional, one per question
        { id: 'q1', earned: 2, possible: 2, label: 'Q1 — source reliability', skill: 'evaluate-sources' },
        { id: 'q2', earned: 0, possible: 3, label: 'Q2 — cause and effect', skill: 'causation' },
      ],
    },
  }
}, '*');
\`\`\`

Rules for \`result\`: \`earned\` and \`possible\` are numbers with 0 ≤ earned ≤ possible and possible no more than 1000; at most 200 items; each item has a string \`id\` that stays the same for that question across attempts; \`label\` and \`skill\` are optional strings. Item marks should add up to the totals. If any part breaks these rules the whole \`result\` is ignored (completion still counts), so check it on the embed checker page, which shows what Kula would record.

**Optional: \`checkpoint\` — at each moment that matters, as it happens.** A moment is something a teacher would want to know about: the learner got something right or wrong, chose a position, or had a piece of writing checked. Send one message per moment, when it happens, not only at the end. Kula stores the latest per step and hands it back on \`lms-init\` (below). A story screen or an explanation is not a moment; an app that is all reading sends none of these, and that is correct.

\`\`\`js
window.parent.postMessage({
  type: 'lms-embed-event', version: '1.0',
  payload: {
    event: 'checkpoint',
    step: 'argue',                      // stable id for this moment, same every time it happens
    attemptId: attemptId,               // the SAME id your eventual completed will carry
    result: { earned: 3, possible: 5, items: [{ id: 'argue', earned: 3, possible: 5 }] },  // only if the moment is marked
    data: { extent: 'to a large extent', line: 'Political will explains the election because…' },
  }
}, '*');
\`\`\`

Rules for \`checkpoint\`: \`step\` is a string of at most 100 characters that never changes once the app is live; \`attemptId\` is the same string you will put on \`completed\`, so the moments and the final marks join up; \`result\` follows the rules above and is omitted (not zeroed) when the moment is not marked; \`data\` is a plain object of strings, numbers and short lists, at most 4 KB as JSON, no HTML; at most 50 distinct steps per attempt. Re-sending a step in the same attempt overwrites it, which is what you want when the learner revises an answer. A message that breaks any rule is dropped whole and the embed checker says why.

**Saved state.** \`lms-init\` carries \`state\`: what Kula already holds for this learner on this lesson, so your app can resume where they left off on any device, and a later app in the lesson can read what an earlier one recorded.

\`\`\`js
// state: null                       → nothing known (staff preview, not enrolled): start fresh
// state.checkpoints: [{ slideId, step, attemptId, result, data, recordedAt }]   // latest per step, every app in the lesson
// state.results:     [{ slideId, attemptId, earned, possible, items, recordedAt }]  // completed run-throughs, newest first
\`\`\`

Filter by \`slideId\` for your own slide's history. Treat \`null\` exactly like empty arrays, and keep your own local save as the fallback: the state arrives with \`lms-init\`, which may never come.

**3. \`advance\` — when the learner CLICKS your final button to leave.** This moves them to the next slide.

\`\`\`js
window.parent.postMessage({
  type: 'lms-embed-event', version: '1.0', payload: { event: 'advance' }
}, '*');
\`\`\`

## Rules

**\`completed\` and \`advance\` are different moments. Do not collapse them.** \`completed\` fires on *arriving* at your closing screen; \`advance\` fires on *choosing* to move on. If you send \`advance\` on arrival you will yank the learner off their own results before they have read a word of them.

**Origin.** ${originLine}

**The app must work standalone as well as embedded.** You will be developing it by opening the URL directly, where there is no lesson listening and \`window.parent\` is the window itself. Wrap every \`postMessage\` in try/catch, and never make the UI wait for an \`lms-init\` that will not arrive — fall back to a sensible anonymous state. An app that only works in one of the two contexts is much harder to debug than one that degrades.

**End on exactly one button, and let it be the exit.** Never write copy telling the learner to press something elsewhere on the page ("tap Done above", "use the menu"). A second, more prominent button that does not actually advance is worse than no button — it reads as broken. Label it for where they are going: "Continue →".

## Check before you finish

- [ ] \`ready\` fires on load, and the app still renders if no \`lms-init\` ever comes back.
- [ ] \`completed\` fires once, when the closing screen appears.
- [ ] If the activity is marked, \`completed\` carries a \`result\` with raw marks and a new \`attemptId\` per run-through, and the embed checker shows the marks rather than "marks ignored".
- [ ] If the app records decisions or marks mid-run, each moment sends a \`checkpoint\` with a stable \`step\` and the attempt's \`attemptId\`, and resuming from \`state\` works when the checker hands your own checkpoints back.
- [ ] \`advance\` fires only from the final button's click handler.
- [ ] Opening the app directly in a browser still works end to end.
- [ ] No copy anywhere points the learner at a control outside your app.
`;
}
